<template>
  <v-card class="elevation-12">
    <v-toolbar color="primary" dark>
      {{ $t('registryPermission.title') }}
    </v-toolbar>
    <v-data-table
        :headers="headers"
        :items="items"
        class="elevation-12"
        hide-default-footer
    >

      <template v-slot:top>
        <v-sheet color="grey lighten-3"
                 class="px-4 pt-4 pb-3"
                 tile>
          <h3 class="transition-swing font-weight-medium">{{ $t('registryPermission.filterTitle') }}</h3>
          <v-responsive>
            <v-row>
              <v-col cols="12" sm="6" md="3" class="py-2">
                <v-autocomplete v-model="organizationId"
                                :items="organizations"
                                :label="$t('registryPermission.labels.organization')"
                                :dense="true"
                                :clearable="true"
                                item-text="name"
                                item-value="id"
                                @change="onFiltersChanged"
                />
              </v-col>
              <v-spacer/>
              <v-col cols="12" sm="6" md="4" class="py-2 text-right">
                <v-btn color="primary" @click="onAddRegistryPermissionButtonClicked">{{ $t('registryPermission.actions.addPermission') }}</v-btn>
                <v-dialog v-model="addRegistryPermissionDialog" :persistent="true" width="600px">
                  <AddRegistryPermissionComponent
                      @cancel="onAddRegistryPermissionCanceled"
                      @added="onAddRegistryPermissionSuccess"
                      ref="addRegistryPermissionComponent"/>
                </v-dialog>
              </v-col>
            </v-row>
          </v-responsive>
        </v-sheet>
      </template>

      <template v-slot:item.createdOn="{ item }">
        {{ formatDate(item.createdOn) }}
      </template>

      <template v-slot:item.actions="{item}">
        <v-btn icon @click="deleteRegistryPermission(item)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <template>
      <template v-if="isDeleteRegistryPermissionSuccess">
        <v-snackbar v-model="isDeleteRegistryPermissionSuccess"
                    :timeout="5000"
                    color="success"
        >
          <v-layout align-center pr-4>
            <v-layout column>
              <div>
                <strong>{{ $t('registryPermission.messages.deleteSuccess') }}</strong>
              </div>
            </v-layout>
          </v-layout>
        </v-snackbar>
      </template>
      <template v-else>
        <v-snackbar v-model="isDeleteRegistryPermissionSuccess"
                    :timeout="5000"
                    color="error"
        >
          <v-layout align-center pr-4>
            <v-layout column>
              <div>
                <strong>{{ $t('registryPermission.messages.deleteError') }}</strong>
              </div>
            </v-layout>
          </v-layout>
        </v-snackbar>
      </template>
    </template>

    <v-snackbar v-model="isAddRegistryPermissionSuccess"
                :timeout="5000"
                :top="true"
                color="success"
    >
      {{ $t('registryPermission.messages.addSuccess') }}
    </v-snackbar>

  </v-card>
</template>


<script>
import _ from "underscore";
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP} from "@/store/modules/layout/actions";
import OrganizationService from "@/services/organization.service";
import Mappers from "@/mappers";
import AddRegistryPermissionComponent from "@/components/registry-permission/AddRegistryPermissionComponent.vue";
import AdminRegistyPermissionService from "@/services/admin.registy.permission.service";
import moment from "moment";

export default {
  name: "RegistryPermissionDataTable",
  components: {
    AddRegistryPermissionComponent
  },
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 1000,
      },
      organizations: [],
      registryPermissions: [],
      organizationId: null,
      isDeleteRegistryPermissionSuccess: false,
      addRegistryPermissionDialog: false,
      isAddRegistryPermissionSuccess: false,
    }
  },
  computed: {
    headers() {
      const headers = []

      headers.push({
        text: this.$t('registryPermission.tableHeaders.number'),
        align: 'center',
        sortable: false,
        value: 'displayNumber'
      })

      headers.push({
        text: this.$t('registryPermission.tableHeaders.organization'),
        align: 'left',
        sortable: false,
        value: 'Organization.name'
      })

      headers.push({
        text: this.$t('registryPermission.tableHeaders.user'),
        align: 'left',
        sortable: false,
        value: 'User.name'
      })

      headers.push({
        text: this.$t('registryPermission.tableHeaders.creationDate'),
        align: 'left',
        sortable: false,
        value: 'createdOn'
      })

      headers.push({
        text: '',
        align: 'right',
        sortable: false,
        value: 'actions'
      })

      return headers
    },
    items() {
      let number = 1;
      let skip = this.skip;
      return _.map(this.registryPermissions, u => {
        u.displayNumber = skip + (number++)
        return u
      })

    },
    skip() {
      return (this.options.page - 1) * this.options.itemsPerPage
    }
  },
  methods: {
    formatDate(dateString) {
      const date = moment(dateString);
      date.locale('en');
      return date.format('DD.MM.yyyy');
    },
    updateItems() {
      this.loadRegistryPermission()
    },
    onFiltersChanged() {
      this.updateItems();
    },
    onAddRegistryPermissionButtonClicked() {
      this.addRegistryPermissionDialog = true
      this.$nextTick(() => {
        this.$refs.addRegistryPermissionComponent.init()
      })
    },
    deleteRegistryPermission(item){
      AdminRegistyPermissionService.delete(item.Id)
          .then(() => {
            this.isDeleteRegistryPermissionSuccess = true
            this.loadRegistryPermission()
          })
    },
    onAddRegistryPermissionCanceled() {
      this.addRegistryPermissionDialog = false
    },
    onAddRegistryPermissionSuccess() {
      this.isAddRegistryPermissionSuccess = true
      this.addRegistryPermissionDialog = false
      this.loadRegistryPermission()
    },
    loadOrganizations() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      OrganizationService.getOrganizations({})
          .then(response => {
            this.organizations = _.map(response.data.Records, o => {
              return Mappers.mapOrganization(o?.Organization)
            })
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },
    loadRegistryPermission(){
      this.loading = true
      this.isDeleteRegistryPermissionSuccess = false
      this.$store.dispatch(LAYOUT_LOADING_START)

      let id = this.organizationId;

      AdminRegistyPermissionService.get(id)
          .then(response => {
            if(response.data.length === 0){
              this.registryPermissions = []
            }
            else{
              this.registryPermissions = _.map(response.data, o => {
                return Mappers.mapRegistryPermission(o)
              })
            }
          })
          .finally(() => {
            this.loading = false
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    }
  },
  created() {
    this.loadOrganizations()
    this.loadRegistryPermission()
    print(this.registryPermissions)

    if (this.$route.query.inn)
      this.filters.inn = this.$route.query.inn
  }
}
</script>

<style scoped>

</style>